import { showSaveToBoardToast } from "@faire/deprecated-retailer-visitor-shared/features/RetailerBoards/Components/SaveToBoardToast";
import { SaveProductData } from "@faire/deprecated-retailer-visitor-shared/features/RetailerBoards/Context";
import { updateBoardsCaches } from "@faire/deprecated-retailer-visitor-shared/features/RetailerBoards/hooks/boardsHooks";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { useProductCacheStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/ProductCacheStore";
import fetchSingleBoard from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/get";
import { getQueryKey as getFetchBoardQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/get-hooks";
import { useMutation as useCreateBoardItemMutation } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/items/post-hooks";
import { useMutation as useDeleteBoardItemMutation } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/products/productToken/delete-hooks";
import { getQueryKey as getFetchBoardProductsByBrandQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/scroll/brands/brandToken/lean-product-tiles/get-hooks";
import { getQueryKey as getFetchBoardBrandsQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/scroll/brands/get-hooks";
import { getQueryKey as getFetchBoardProductsQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/scroll/lean-product-tiles/get-hooks";
import fetchDefaultBoard from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/default/get";
import { getQueryKey as getFetchDefaultBoardQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/default/get-hooks";
import { ICreateRetailerBoardItemRequest } from "@faire/web-api--source/indigofair/data/ICreateRetailerBoardItemRequest";
import { IGetBoardItemsGroupByBrandsScrollResponse } from "@faire/web-api--source/indigofair/data/IGetBoardItemsGroupByBrandsScrollResponse";
import { IGetRetailerBoardLeanProductTilesScrollResponse } from "@faire/web-api--source/indigofair/data/IGetRetailerBoardLeanProductTilesScrollResponse";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IRetailerBoard } from "@faire/web-api--source/indigofair/data/IRetailerBoard";
import {
  InfiniteData,
  QueryClient,
  useQueryClient,
} from "@tanstack/react-query";
import React from "react";

export const useSaveToBoard = (onToastChangeBoardClick?: () => void) => {
  const { addToBoard } = useAddToBoard();

  const saveToBoard = React.useCallback(
    async (
      productToken: string,
      brandToken: string | undefined,
      boardTokenToUse: string | undefined,
      productImage?: IImage,
      onUpdate?: (data: SaveProductData) => void
    ) => {
      const { boardToken } = await addToBoard(
        productToken,
        brandToken,
        boardTokenToUse,
        productImage,
        onUpdate
      );
      showSaveToBoardToast({
        boardToken,
        productImage,
        onChangeBoardClick: onToastChangeBoardClick,
      });
      return { boardToken };
    },
    [addToBoard, onToastChangeBoardClick]
  );

  return { saveToBoard };
};

const useAddToBoard = () => {
  const mutation = useCreateBoardItemMutation();
  const queryClient = useQueryClient();
  const retailerToken = getRetailer()?.token;
  const maybeRetailerToken = retailerToken ?? "";
  const { invalidateCache } = useProductCacheStore(["invalidateCache"]);

  const addToBoard = React.useCallback(
    async (
      productToken: string,
      brandToken: string | undefined,
      boardToken: string | undefined,
      productImage?: IImage,
      onUpdate?: (data: SaveProductData) => void
    ) => {
      let board: IRetailerBoard | undefined;
      if (!boardToken) {
        const response = await queryClient.fetchQuery({
          queryKey: getFetchDefaultBoardQueryKey(maybeRetailerToken),
          queryFn: () => fetchDefaultBoard(maybeRetailerToken),
          staleTime: 5 * 60 * 1000,
        });
        board = response.board;
      } else {
        const response = await queryClient.fetchQuery({
          queryKey: getFetchBoardQueryKey(maybeRetailerToken, boardToken),
          queryFn: () => fetchSingleBoard(maybeRetailerToken, boardToken),
          staleTime: 5 * 60 * 1000,
        });
        board = response?.board;
      }

      const request = ICreateRetailerBoardItemRequest.build({
        image_data: ICreateRetailerBoardItemRequest.IImageData.build({
          url: productImage?.url,
        }),
        product_token: productToken,
      });

      const response = await mutation.mutateAsync([
        maybeRetailerToken,
        board?.token ?? "",
        request,
      ]);

      updateBoardsCaches(
        queryClient,
        maybeRetailerToken,
        response.item?.board_token ?? "",
        {
          ...board,
          items_count: (board?.items_count ?? 0) + 1,
          cover_images: [
            IImage.build(productImage),
            ...(board?.cover_images ?? []),
          ],
        }
      );
      refetchBoardItemsQueries(
        queryClient,
        maybeRetailerToken,
        response.item?.token ?? "",
        brandToken ?? ""
      );
      onUpdate?.({ savedToBoardToken: board?.token });
      invalidateCache(brandToken ?? "", queryClient);

      return { boardToken: response.retailer_board?.token };
    },
    [mutation, queryClient, maybeRetailerToken, invalidateCache]
  );

  return { addToBoard };
};

export const useRemoveFromBoard = () => {
  const mutation = useDeleteBoardItemMutation();
  const queryClient = useQueryClient();
  const retailerToken = getRetailer()?.token;
  const maybeRetailerToken = retailerToken ?? "";
  const { invalidateCache } = useProductCacheStore(["invalidateCache"]);

  const removeFromBoard = React.useCallback(
    async (
      productToken: string,
      boardToken: string,
      brandToken: string | undefined,
      onUpdate?: (data: SaveProductData) => void
    ) => {
      // we update the items cache before performing the request as an optimistic UI trick
      updateBoardItemsCaches(
        queryClient,
        maybeRetailerToken,
        boardToken,
        brandToken ?? "",
        productToken
      );
      // then we immediately fire onUpdate callback to show optimistic updated data to user
      onUpdate?.({ removedFromBoardToken: boardToken });

      const response = await mutation.mutateAsync([
        maybeRetailerToken,
        boardToken,
        productToken,
      ]);

      // we update the boards cache after the request to use fresh board data form the response
      updateBoardsCaches(
        queryClient,
        maybeRetailerToken,
        boardToken,
        IRetailerBoard.build(response.retailer_board)
      );
      // then we fire onUpdate callback again without any token, just to get data in sync
      onUpdate?.({});
      invalidateCache(brandToken, queryClient);

      return response;
    },
    [mutation, queryClient, maybeRetailerToken, invalidateCache]
  );

  return { removeFromBoard };
};

export const useMoveToBoard = () => {
  const { addToBoard } = useAddToBoard();
  const { removeFromBoard } = useRemoveFromBoard();

  const moveToBoard = React.useCallback(
    async (
      productToken: string,
      brandToken: string | undefined,
      fromBoardToken: string,
      toBoardToken: string,
      productImage?: IImage,
      onUpdate?: (data: SaveProductData) => void
    ) => {
      await removeFromBoard(productToken, fromBoardToken, brandToken, onUpdate);
      const { boardToken } = await addToBoard(
        productToken,
        brandToken,
        toBoardToken,
        productImage,
        onUpdate
      );

      showSaveToBoardToast({
        boardToken,
        productImage,
        onChangeBoardClick: undefined,
        boardItemIsMoving: true,
      });

      return { boardToken };
    },
    [addToBoard, removeFromBoard]
  );

  return { moveToBoard };
};

const refetchBoardItemsQueries = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  brandToken: string
) => {
  void queryClient.refetchQueries({
    queryKey: getFetchBoardProductsQueryKey(retailerToken, boardToken),
  });
  void queryClient.refetchQueries({
    queryKey: getFetchBoardBrandsQueryKey(retailerToken, boardToken),
  });
  void queryClient.refetchQueries({
    queryKey: getFetchBoardProductsByBrandQueryKey(
      retailerToken,
      boardToken,
      brandToken
    ),
  });
};

const updateBoardItemsCaches = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  brandToken: string,
  productToken: string
) => {
  updateBoardProductsCache(
    queryClient,
    retailerToken,
    boardToken,
    productToken
  );
  updateBoardBrandsCache(
    queryClient,
    retailerToken,
    boardToken,
    brandToken,
    productToken
  );
  updateBoardProductsByBrandCache(
    queryClient,
    retailerToken,
    boardToken,
    brandToken,
    productToken
  );
};

const updateBoardProductsCache = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  productToken: string
) => {
  // this cache is used for board products in the board page organized by product
  const fetchBoardProductsKey = getFetchBoardProductsQueryKey(
    retailerToken,
    boardToken
  );
  queryClient.setQueryData<
    InfiniteData<IGetRetailerBoardLeanProductTilesScrollResponse>
  >(fetchBoardProductsKey, (cached) => {
    if (!cached) {
      return;
    }
    const cachedPage = cached.pages.find((page) =>
      page.product_tiles.find((tile) => tile.product?.token === productToken)
    );
    if (cachedPage) {
      cachedPage.product_tiles = cachedPage.product_tiles.filter(
        (tile) => tile.product?.token !== productToken
      );
    }
    return cached;
  });
};

const updateBoardBrandsCache = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  brandToken: string,
  productToken: string
) => {
  // this cache is used for board brands in the board page organized by brand
  const fetchBoardBrandsKey = getFetchBoardBrandsQueryKey(
    retailerToken,
    boardToken
  );
  queryClient.setQueryData<
    InfiniteData<IGetBoardItemsGroupByBrandsScrollResponse>
  >(fetchBoardBrandsKey, (cached) => {
    if (!cached) {
      return;
    }
    const cachedPage = cached.pages.find(
      (page) => page.brand_token_to_items[brandToken]
    );
    const filteredItems = cachedPage?.brand_token_to_items[
      brandToken
    ]?.items.filter((item) => item.product?.token !== productToken);
    if (cachedPage && filteredItems) {
      if (filteredItems.length) {
        cachedPage.brand_token_to_items[brandToken] =
          IGetBoardItemsGroupByBrandsScrollResponse.IRetailerBoardItemsCollection.build(
            {
              ...cachedPage.brand_token_to_items[brandToken],
              items: filteredItems,
              product_count: filteredItems.length,
            }
          );
      } else {
        delete cachedPage.brand_token_to_items[brandToken];
      }
    }
    return cached;
  });
};

const updateBoardProductsByBrandCache = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  brandToken: string,
  productToken: string
) => {
  // this cache is used for board products in the board page organized by brand
  const fetchBoardProductsByBrandKey = getFetchBoardProductsByBrandQueryKey(
    retailerToken,
    boardToken,
    brandToken
  );
  queryClient.setQueryData<
    InfiniteData<IGetRetailerBoardLeanProductTilesScrollResponse>
  >(fetchBoardProductsByBrandKey, (cached) => {
    if (!cached) {
      return;
    }
    const cachedPage = cached.pages.find((page) =>
      page.product_tiles.find((tile) => tile.product?.token === productToken)
    );
    if (cachedPage) {
      cachedPage.product_tiles = cachedPage.product_tiles.filter(
        (tile) => tile.product?.token !== productToken
      );
    }
    return cached;
  });
};
